import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query, where, getCountFromServer } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useLanguage } from '@/contexts/LanguageContext';
import { LanguageSelector } from '@/components/ui/language-selector';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Crown, Clock, Check, Copy } from 'lucide-react';

// ── EXPIRY DATE (UTC pour la synchronisation) ──
const EXPIRY_DATE = new Date('2026-07-31T23:59:59Z');

const t = {
  en: {
    navHome: 'Home',
    badge: 'Founding Partners Program',
    title: 'Join the ',
    titleAccent: 'Founding Partners',
    subtitle:
      'AcquizGroup opens its Internal Partners program to a restricted circle — priority beta access, permanent Founder badge, and lifetime pricing lock-in with code EXPERTS30.',
    countSuffix: ' founding partners already registered',
    expiresLabel: 'Offer expires in',
    days: 'd',
    hours: 'h',
    minutes: 'm',
    seconds: 's',
    limitedSpots: 'Limited to 100 founding partners',

    pricingTitle: 'Founding Partner Pricing — Internal Partners',
    pricingSubtitle:
      'Lock in an extra -30% off launch prices forever. Applied automatically with code EXPERTS30 at checkout.',
    partnerTypesTitle: 'Open to:',
    partnerTypes:
      'Business Lawyer · Immigration Lawyer · Notary · CPA · Tax Advisor',

    regular: 'Regular',
    launch: 'Launch',
    founding: 'Founding',
    perMonth: '/mo',
    save: 'Save',
    mostPopular: 'Most Popular',
    bestValue: 'Best Value',
    couponBox: 'Use code EXPERTS30 at checkout',
    ctaFounding: 'Register as Founding Partner →',
    sectionNote:
      '* Code EXPERTS30 applied automatically at checkout. Expires July 31, 2026.',

    benefitsTitle: 'Founding Partner Benefits',
    benefit1Title: '"Our Experts Founder" Badge',
    benefit1Desc:
      'A permanent blue-bordered badge on your public profile — visible to all platform users forever.',
    benefit2Title: '-30% Extra on Launch Price — For Life',
    benefit2Desc:
      'An additional 30% off our already-discounted launch prices, applied at every renewal. Forever.',
    benefit3Title: 'Priority Beta Access',
    benefit3Desc:
      'Access the Internal Partners module before the public launch. Onboard your first clients early.',
    benefit4Title: 'Your Voice Shapes the Platform',
    benefit4Desc:
      'Direct line to the founding team for feature requests and product feedback.',
  },
  fr: {
    navHome: 'Accueil',
    badge: 'Programme Partenaires Fondateurs',
    title: 'Rejoignez les ',
    titleAccent: 'Partenaires Fondateurs',
    subtitle:
      "AcquizGroup ouvre son programme Partenaires Internes à un cercle restreint — accès bêta prioritaire, badge Fondateur permanent, et tarif bloqué à vie avec le code EXPERTS30.",
    countSuffix: ' partenaires fondateurs déjà inscrits',
    expiresLabel: 'Offre disponible encore',
    days: 'j',
    hours: 'h',
    minutes: 'm',
    seconds: 's',
    limitedSpots: 'Limité à 100 partenaires fondateurs',

    pricingTitle: 'Tarifs Partenaires Fondateurs — Partenaires Internes',
    pricingSubtitle:
      'Bloquez -30% supplémentaires sur les prix de lancement à vie. Appliqué automatiquement avec le code EXPERTS30 au paiement.',
    partnerTypesTitle: 'Ouvert à :',
    partnerTypes:
      'Avocat d\'affaires · Avocat en immigration · Notaire · CPA · Fiscaliste',

    regular: 'Régulier',
    launch: 'Lancement',
    founding: 'Fondateur',
    perMonth: '/mois',
    save: 'Économisez',
    mostPopular: 'Le Plus Populaire',
    bestValue: 'Meilleure Valeur',
    couponBox: 'Utilisez le code EXPERTS30 au paiement',
    ctaFounding: "S'inscrire comme Partenaire Fondateur →",
    sectionNote:
      '* Le code EXPERTS30 est appliqué automatiquement au paiement. Expire le 31 juillet 2026.',

    benefitsTitle: 'Avantages Partenaire Fondateur',
    benefit1Title: 'Badge "Our Experts Founder"',
    benefit1Desc:
      'Un badge permanent avec bordure bleue sur votre profil public — visible par tous les utilisateurs de la plateforme pour toujours.',
    benefit2Title: '-30% Supplémentaire sur le Prix de Lancement — À Vie',
    benefit2Desc:
      "Une réduction supplémentaire de 30% sur nos prix de lancement déjà réduits, appliquée à chaque renouvellement. Pour toujours.",
    benefit3Title: 'Accès Bêta Prioritaire',
    benefit3Desc:
      "Accédez au module Partenaires Internes avant le lancement public. Intégrez vos premiers clients tôt.",
    benefit4Title: 'Votre Voix Compte',
    benefit4Desc:
      "Ligne directe avec l'équipe fondatrice pour vos demandes de fonctionnalités et retours produit.",
  },
};

function useCountdown(target: Date) {
  const calc = () => {
    const diff = target.getTime() - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
    };
  };
  const [time, setTime] = useState(calc);
  useEffect(() => {
    const id = setInterval(() => setTime(calc()), 1000);
    return () => clearInterval(id);
  }, []);
  return time;
}

// ── COMPTEUR RÉEL — nombre de Partenaires inscrits (Firestore) ──
// Rafraîchi toutes les 60s pendant que la page reste ouverte.
function useRealFoundingCounter(fallback: number) {
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchCount = async () => {
      try {
        const q = query(
          collection(db, 'users'),
          where('roles', 'array-contains', 'partner')
        );
        const snap = await getCountFromServer(q);
        if (!cancelled) setCount(snap.data().count);
      } catch (err) {
        console.warn('[EarlyAccessPartners] founding counter fetch failed:', err);
        // On garde la valeur précédente (ou le fallback) plutôt que de casser l'affichage
      }
    };

    fetchCount();
    const interval = setInterval(fetchCount, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  return count ?? fallback;
}

type PlanCard = {
  name: string;
  badge?: 'popular' | 'best';
  regular: number;
  launch: number;
  founding: number;
  launchDiscountLabel: string;
  foundingDiscountLabel: string;
  saveLabel: string;
  features: string[];
  ctaPath: string;
};

export default function EarlyAccessPartners() {
  const { language } = useLanguage();
  const lang = (language as 'en' | 'fr') || 'en';
  const labels = t[lang];
  const countdown = useCountdown(EXPIRY_DATE);
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  // Note : le compteur réel (useRealFoundingCounter) reste disponible dans ce fichier
  // pour être réactivé facilement plus tard, une fois que le nombre d'inscriptions
  // réelles sera assez élevé pour être affiché. Non utilisé pour l'instant.

  const isExpired = EXPIRY_DATE.getTime() <= Date.now();

  const copyCoupon = () => {
    navigator.clipboard?.writeText('EXPERTS30');
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  // ── PARTNER PLANS (monthly) ──
  const partnerPlans: PlanCard[] = [
    {
      name: 'Basic',
      regular: 149,
      launch: 99,
      founding: 69,
      launchDiscountLabel: '-34%',
      foundingDiscountLabel: '-30% extra',
      saveLabel: `$80${labels.perMonth} · 54% ${lang === 'fr' ? 'vs régulier' : 'off regular'}`,
      features:
        lang === 'fr'
          ? [
              '1 rôle professionnel',
              'Dashboard Partenaire Interne',
              'Annuaire clients',
              'Messagerie sécurisée',
              'Calendrier + visioconférence',
            ]
          : [
              '1 professional role',
              'Internal Partner dashboard',
              'Client directory',
              'Secure messaging',
              'Calendar + video conferencing',
            ],
      ctaPath: '/register/partner?promo=EXPERTS30&plan=partner_basic',
    },
    {
      name: 'Pro',
      badge: 'popular',
      regular: 299,
      launch: 199,
      founding: 139,
      launchDiscountLabel: '-33%',
      foundingDiscountLabel: '-30% extra',
      saveLabel: `$160${labels.perMonth} · 54% ${lang === 'fr' ? 'vs régulier' : 'off regular'}`,
      features:
        lang === 'fr'
          ? [
              'Tout du forfait Basic',
              'Badge vérifié sur profil public',
              'Data Room partagée',
              'Analytique des mandats',
              'Visibilité sur les deals de votre secteur',
            ]
          : [
              'All Basic benefits',
              'Verified badge on public profile',
              'Shared Data Room',
              'Mandate analytics',
              'Visibility on deals in your sector',
            ],
      ctaPath: '/register/partner?promo=EXPERTS30&plan=partner_pro',
    },
    {
      name: 'Elite',
      badge: 'best',
      regular: 499,
      launch: 349,
      founding: 244,
      launchDiscountLabel: '-30%',
      foundingDiscountLabel: '-30% extra',
      saveLabel: `$255${labels.perMonth} · 51% ${lang === 'fr' ? 'vs régulier' : 'off regular'}`,
      features:
        lang === 'fr'
          ? [
              'Tout du forfait Pro',
              'Profil mis en avant sur TOUS les listings pertinents',
              '1 Étude de marché incluse/trimestre',
              'Support AcquizGroup dédié',
              'Priorité sur les demandes de contact acheteurs',
            ]
          : [
              'All Pro benefits',
              'Profile featured on ALL relevant listings',
              '1 Market Study included/quarter',
              'Dedicated AcquizGroup support',
              'Priority on buyer contact requests',
            ],
      ctaPath: '/register/partner?promo=EXPERTS30&plan=partner_elite',
    },
  ];

  const renderBadge = (badge?: PlanCard['badge']) => {
    if (!badge) return null;
    if (badge === 'popular') {
      return (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#CEAB65] text-[#1D314D] text-xs font-bold px-3 py-1 rounded-full shadow-lg">
          {labels.mostPopular}
        </div>
      );
    }
    return (
      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#1D314D] text-[#CEAB65] border border-[#CEAB65] text-xs font-bold px-3 py-1 rounded-full shadow-lg">
        {labels.bestValue}
      </div>
    );
  };

  const PlanCardView = ({ plan }: { plan: PlanCard }) => {
    return (
      <div
        className={`relative bg-[#1D314D] border rounded-xl p-6 flex flex-col ${
          plan.badge === 'popular'
            ? 'border-[#CEAB65]/70 shadow-[0_0_30px_-10px_rgba(206,171,101,0.5)]'
            : plan.badge === 'best'
              ? 'border-[#CEAB65]/40'
              : 'border-white/10'
        }`}
      >
        {renderBadge(plan.badge)}

        <h4 className="text-lg font-bold mb-4 mt-2">{plan.name}</h4>

        {/* Pricing block */}
        <div className="space-y-1 mb-4">
          <div className="flex items-baseline gap-2">
            <span className="text-gray-400 line-through text-sm">
              ${plan.regular}
              {labels.perMonth}
            </span>
            <span className="text-gray-400 text-xs uppercase">{labels.regular}</span>
          </div>
          <div className="flex items-baseline gap-2">
            <span className="text-white font-semibold">
              🚀 ${plan.launch}
              {labels.perMonth}
            </span>
            <span className="text-white/60 text-xs">({plan.launchDiscountLabel})</span>
          </div>
          <div className="flex items-baseline gap-2 pt-1">
            <span className="text-[#CEAB65] text-3xl font-bold">
              ${plan.founding}
            </span>
            <span className="text-[#CEAB65] text-sm">{labels.perMonth}</span>
            <span className="text-[#CEAB65]/80 text-xs">
              ({plan.foundingDiscountLabel})
            </span>
          </div>
          <div className="inline-block bg-green-500/20 text-green-300 border border-green-500/40 rounded-full px-3 py-1 text-xs font-semibold mt-2">
            {labels.save} {plan.saveLabel}
          </div>
        </div>

        {/* Features */}
        <ul className="space-y-2 mb-6 flex-grow">
          {plan.features.map((f, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-white/80">
              <Check className="w-4 h-4 text-[#CEAB65] flex-shrink-0 mt-0.5" />
              <span>{f}</span>
            </li>
          ))}
        </ul>

        {/* Coupon pill */}
        <button
          onClick={copyCoupon}
          className="flex items-center justify-center gap-2 w-full bg-[#CEAB65]/15 border border-[#CEAB65]/50 text-[#CEAB65] rounded-full px-3 py-1.5 text-xs font-mono font-semibold mb-3 hover:bg-[#CEAB65]/25 transition-colors"
          type="button"
        >
          <Copy className="w-3 h-3" />
          {copied ? '✓ EXPERTS30' : labels.couponBox}
        </button>

        {/* CTA */}
        <Button
          onClick={() => navigate(plan.ctaPath)}
          className="w-full bg-[#CEAB65] text-[#1D314D] hover:bg-[#b8954f] font-bold py-5"
          disabled={isExpired}
        >
          {labels.ctaFounding}
        </Button>
      </div>
    );
  };

  const benefits = [
    { title: labels.benefit1Title, desc: labels.benefit1Desc },
    { title: labels.benefit2Title, desc: labels.benefit2Desc },
    { title: labels.benefit3Title, desc: labels.benefit3Desc },
    { title: labels.benefit4Title, desc: labels.benefit4Desc },
  ];

  return (
    <div className="min-h-screen bg-[#0A1628] text-white">
      {/* NAV */}
      <nav className="border-b border-white/10 bg-[#0A1628]/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="text-xl font-bold tracking-tight">
            Acquiz<span className="text-[#CEAB65]">Group</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSelector variant="light" />
            <Button
              asChild
              variant="outline"
              size="sm"
              className="border-white/30 bg-white/10 text-white hover:bg-white/20 hover:text-white"
            >
              <Link to="/">{labels.navHome}</Link>
            </Button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative overflow-hidden py-20 sm:py-28">
        <div className="absolute inset-0 bg-gradient-to-br from-[#CEAB65]/10 via-transparent to-[#1D314D]/40 pointer-events-none" />
        <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#CEAB65]/20 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Badge className="mb-6 bg-[#CEAB65]/20 text-[#CEAB65] border border-[#CEAB65]/40 hover:bg-[#CEAB65]/20 px-3 py-1">
            <Crown className="w-3.5 h-3.5 mr-1.5" />
            {labels.badge}
          </Badge>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
            {labels.title}
            <span className="text-[#CEAB65]">{labels.titleAccent}</span>
          </h1>

          <p className="text-lg text-white/70 mb-6 max-w-2xl mx-auto leading-relaxed">
            {labels.subtitle}
          </p>

          <p className="text-sm text-white/60 mb-10">
            <span className="text-[#CEAB65] font-semibold">{labels.partnerTypesTitle}</span>{' '}
            {labels.partnerTypes}
          </p>

          {/* Countdown */}
          {!isExpired && (
            <div className="mb-8">
              <div className="flex items-center justify-center gap-2 text-xs uppercase tracking-wider text-white/60 mb-3">
                <Clock className="w-3.5 h-3.5" />
                {labels.expiresLabel}
              </div>
              <div className="flex justify-center gap-3">
                {[
                  { val: countdown.days, label: labels.days },
                  { val: countdown.hours, label: labels.hours },
                  { val: countdown.minutes, label: labels.minutes },
                  { val: countdown.seconds, label: labels.seconds },
                ].map(({ val, label }) => (
                  <div
                    key={label}
                    className="bg-white/5 border border-white/10 rounded-lg px-4 py-3 min-w-[64px]"
                  >
                    <div className="text-2xl font-bold text-[#CEAB65] tabular-nums">
                      {val.toString().padStart(2, '0')}
                    </div>
                    <div className="text-[10px] uppercase tracking-wider text-white/50 mt-0.5">
                      {label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Urgency badge — no live headcount displayed, cap policy only */}
          <p className="inline-flex items-center gap-2 text-sm text-[#CEAB65] border border-[#CEAB65]/40 bg-[#CEAB65]/10 rounded-full px-4 py-1.5">
            🎖 {labels.limitedSpots}
          </p>
        </div>
      </section>

      {/* PARTNER PRICING */}
      <section className="py-20 border-t border-white/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-3">
            {labels.pricingTitle}
          </h2>
          <p className="text-center text-white/60 mb-12 max-w-2xl mx-auto">
            {labels.pricingSubtitle}
          </p>

          <div className="grid gap-6 md:grid-cols-3">
            {partnerPlans.map((plan) => (
              <PlanCardView key={plan.name} plan={plan} />
            ))}
          </div>

          <p className="text-xs text-white/50 text-center mt-8">{labels.sectionNote}</p>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-20 border-t border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl font-bold text-center mb-12">
            {labels.benefitsTitle}
          </h2>
          <div className="grid gap-6 md:grid-cols-2">
            {benefits.map((b) => (
              <div
                key={b.title}
                className="bg-[#1D314D] border border-white/10 rounded-xl p-6"
              >
                <h3 className="text-lg font-bold text-[#CEAB65] mb-2">{b.title}</h3>
                <p className="text-white/70 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/10 py-8 text-center text-xs text-white/50">
        © 2026 AcquizGroup. All rights reserved.
      </footer>
    </div>
  );
}